import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Building2, Mail, Lock, ArrowRight, TrendingUp, Shield, Zap, Eye, EyeOff } from 'lucide-react';
import TwoFactorVerifyModal from '../components/TwoFactorVerifyModal';
import EmailCodeVerifyModal from '../components/EmailCodeVerifyModal';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [showEmailCode, setShowEmailCode] = useState(false);
  const [emailCodeAddress, setEmailCodeAddress] = useState('');
  const hasRedirectedRef = useRef(false);
  const navigate = useNavigate();
  const { login, verifyTwoFactor, verifyEmailCode, resendEmailCode, user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && user && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      navigate('/', { replace: true });
    }
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin" />
      </div>
    );
  }

  if (user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const { error, requiresTwoFactor, secret, requiresEmailCode, userEmail } = await login(email, password);
      if (error) {
        setError(error);
      } else if (requiresTwoFactor && secret) {
        setTwoFactorToken(secret);
        setShowTwoFactor(true);
      } else if (requiresEmailCode) {
        setEmailCodeAddress(userEmail || email);
        setShowEmailCode(true);
      }
    } catch {
      setError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorVerify = async (token: string) => {
    const { error } = await verifyTwoFactor(token);
    if (error) {
      setError(error);
      return false;
    }
    setShowTwoFactor(false);
    return true;
  };

  const features = [
    { icon: TrendingUp, label: 'Suivi en temps réel',  desc: 'Transactions et performances instantanées' },
    { icon: Shield,     label: 'Sécurisé',              desc: 'Authentification 2FA & données chiffrées' },
    { icon: Zap,        label: 'Interface rapide',      desc: 'Conçue pour gagner du temps au quotidien' },
  ];

  return (
    <div className="min-h-screen flex">

      {/* ─── Panneau gauche — branding ──────────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-[52%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #060c18 0%, #0c1220 50%, #0e1130 100%)' }}
      >
        {/* Cercles décoratifs */}
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)' }} />
        <div className="absolute top-1/3 right-1/4 w-[200px] h-[200px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)' }} />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)', boxShadow: '0 0 20px rgba(99,102,241,0.4)' }}>
            <Building2 size={20} className="text-white" />
          </div>
          <div>
            <p className="text-base font-bold text-white tracking-wider leading-none">K-PROD</p>
            <p className="text-[11px] text-slate-500 font-medium tracking-wide mt-0.5">Gestion des transactions</p>
          </div>
        </div>

        {/* Texte + features */}
        <div className="relative z-10 space-y-10">
          <div>
            <h1 className="text-[2.6rem] font-bold text-white leading-[1.15] tracking-tight">
              Gérez vos<br />
              <span className="text-indigo-400">transactions</span><br />
              efficacement
            </h1>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-xs">
              La plateforme complète pour suivre, valider et analyser toutes vos ventes immobilières.
            </p>
          </div>

          <div className="space-y-3">
            {features.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="flex items-center gap-4 p-3.5 rounded-2xl"
                style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.2)' }}>
                  <Icon size={16} className="text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white leading-none">{label}</p>
                  <p className="text-xs text-slate-500 mt-1">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-xs text-slate-600">© 2025 K-PROD — Tous droits réservés</p>
        </div>
      </div>

      {/* ─── Panneau droit — formulaire ─────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-slate-50 relative">

        {/* Motif de fond subtil */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(99,102,241,0.05) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(139,92,246,0.04) 0%, transparent 50%)' }} />

        {/* Logo mobile */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)' }}>
            <Building2 size={18} className="text-white" />
          </div>
          <span className="text-lg font-bold text-slate-800 tracking-wide">K-PROD</span>
        </div>

        {/* Card formulaire */}
        <div className="relative z-10 w-full max-w-[400px]">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200/80 p-8">

            {/* En-tête */}
            <div className="mb-7">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)', boxShadow: '0 4px 16px rgba(99,102,241,0.35)' }}>
                <Building2 size={22} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Bon retour</h2>
              <p className="text-sm text-slate-400 mt-1">Connectez-vous à votre espace de gestion</p>
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Adresse email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail size={15} className="text-slate-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    required
                    disabled={loading}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors disabled:opacity-50 bg-white"
                  />
                </div>
              </div>

              {/* Mot de passe */}
              <div>
                <label htmlFor="password" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Lock size={15} className="text-slate-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    disabled={loading}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-10 py-2.5 text-sm border border-slate-200 rounded-xl text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors disabled:opacity-50 bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Erreur */}
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Bouton */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-60 mt-2"
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
                  boxShadow: loading ? 'none' : '0 4px 16px rgba(99,102,241,0.35)',
                }}
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    <span>Connexion…</span>
                  </>
                ) : (
                  <>
                    <span>Se connecter</span>
                    <ArrowRight size={15} />
                  </>
                )}
              </button>
            </form>

            {/* Info sécurité */}
            <div className="flex items-center justify-center gap-1.5 mt-6">
              <Shield size={12} className="text-slate-300" />
              <span className="text-xs text-slate-400">Connexion sécurisée — données protégées</span>
            </div>
          </div>

          {/* Badge version */}
          <p className="text-center text-xs text-slate-400 mt-5">K-PROD · Gestion de transactions immobilières</p>
        </div>
      </div>

      <TwoFactorVerifyModal
        isOpen={showTwoFactor}
        onClose={() => { setShowTwoFactor(false); setLoading(false); }}
        onVerify={handleTwoFactorVerify}
        secret={twoFactorToken}
      />

      <EmailCodeVerifyModal
        isOpen={showEmailCode}
        email={emailCodeAddress}
        onClose={() => { setShowEmailCode(false); setLoading(false); }}
        onVerify={async (code) => {
          const { error } = await verifyEmailCode(code);
          if (error) { setError(error); return false; }
          setShowEmailCode(false);
          return true;
        }}
        onResend={resendEmailCode}
      />
    </div>
  );
}
